import React, { useEffect, useState } from "react";
import { FEATURED_REPOS, INCLUDE_FORKS, MAX_REPOS } from "../constants";

const ProjectCard = ({ title, image, description, git, technologies }) => (
    <div className="flex h-[420px] w-80 max-w-xs flex-col justify-between overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/70 shadow-[0_18px_45px_rgba(15,23,42,0.9)] transition-transform duration-200 hover:-translate-y-2 hover:shadow-[0_26px_70px_rgba(15,23,42,1)]">
        <div>
            <div className="p-4 pb-2">
                <h5 className="truncate text-xl font-semibold text-slate-50">{title}</h5>
                <p className="mt-1 line-clamp-3 text-sm text-slate-300">
                    {description || "No description provided yet."}
                </p>
            </div>
            <img
                src={image}
                alt={title}
                loading="lazy"
                className="h-56 w-full bg-slate-800 object-fill"
                onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = 'logo_home.png';
                }}
            />
        </div>
        <div className="flex flex-1 flex-col justify-end">
            <div className="flex flex-wrap gap-2 px-4 pb-2">
                {technologies && technologies.length > 0 ? (
                    technologies.map((tag, index) => (
                        <span
                            key={index}
                            className="rounded bg-sky-900/40 px-2 py-0.5 text-xs text-sky-300"
                        >
                                #{tag}
                            </span>
                    ))
                ) : (
                    <span className="text-xs text-slate-500">#github</span>
                )}
            </div>
            <div className="px-4 pb-4">
                <a
                    href={git}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-lg border border-slate-600 bg-slate-900/80 py-2 text-center text-sm font-medium text-slate-100 transition-colors duration-200 hover:border-sky-400 hover:bg-sky-500/90 hover:text-slate-950"
                >
                    View on GitHub
                </a>
            </div>
        </div>
    </div>
);

const Projects = () => {
    const [repos, setRepos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchRepos = async () => {
            try {
                const res = await fetch("https://api.github.com/users/abhay25v/repos");
                if (!res.ok) {
                    throw new Error("Failed to load repositories");
                }
                const data = await res.json();

                const all = (Array.isArray(data) ? data : []).filter((repo) =>
                    INCLUDE_FORKS ? true : !repo.fork
                );

                const featured = Array.isArray(FEATURED_REPOS) ? FEATURED_REPOS.filter(Boolean) : [];
                if (featured.length > 0) {
                    const byFullName = new Map(all.map((repo) => [repo.full_name, repo]));
                    const ordered = featured
                        .map((fullName) => byFullName.get(fullName))
                        .filter(Boolean);
                    setRepos(ordered);
                    return;
                }

                const maxCount = Number.isFinite(MAX_REPOS) ? Math.max(1, MAX_REPOS) : 8;
                const filtered = all
                    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
                    .slice(0, maxCount);
                setRepos(filtered);
            } catch (err) {
                setError(err.message || "Something went wrong while fetching repos.");
            } finally {
                setLoading(false);
            }
        };

        fetchRepos();
    }, []);

    return (
        <section id="projects" className="min-h-screen w-full bg-gradient-to-b from-slate-950 via-slate-900 to-black text-white scroll-mt-24">
            <div className="mx-auto max-w-6xl px-4 pt-28 pb-16 reveal-on-scroll">
                <div className="mb-8 text-center">
                    <p className="text-xs font-medium uppercase tracking-[0.3em] text-sky-400/80">
                        Projects
                    </p>
                    <h2 className="mt-2 text-3xl font-extrabold sm:text-4xl md:text-5xl">
                        <span className="bg-gradient-to-r from-sky-300 via-teal-300 to-emerald-300 bg-clip-text text-transparent">
                            GitHub Highlights
                        </span>
                    </h2>
                    <p className="mx-auto mt-3 max-w-2xl text-sm sm:text-base text-slate-300">
                        These projects are pulled directly from my public GitHub profile.
                        They showcase experiments, learning projects, and end‑to‑end builds I am
                        currently exploring.
                    </p>
                </div>

                {loading && (
                    <div className="flex items-center justify-center py-16 text-slate-400">
                        Loading repositories...
                    </div>
                )}

                {!loading && error && (
                    <div className="flex items-center justify-center py-16 text-sm text-red-300">
                        {error}
                    </div>
                )}

                {!loading && !error && (
                    <div className="flex flex-wrap items-stretch justify-center gap-8">
                        {repos.length === 0 ? (
                            <p className="mt-8 text-sm text-slate-400">
                                No repositories found yet. Check back soon!
                            </p>
                        ) : (
                            repos.map((repo) => (
                                <ProjectCard
                                    key={repo.id}
                                    title={repo.name}
                                    image={repo.full_name
                                        ? `https://opengraph.githubassets.com/1/${repo.full_name}`
                                        : repo.owner?.avatar_url}
                                    description={repo.description}
                                    git={repo.html_url}
                                    technologies={repo.topics || []}
                                />
                            ))
                        )}
                    </div>
                )}
            </div>
        </section>
    );
};

export default Projects;